import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PendingFulfillmentList.css';

function PendingFulfillmentList() {
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'requirements'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sortColumn, setSortColumn] = useState('tranDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterShippable, setFilterShippable] = useState('all'); // 'all', 'fully', 'partial', 'none'
  const [expandedOrders, setExpandedOrders] = useState({}); // Track expanded orders
  const [orderLineItems, setOrderLineItems] = useState({}); // Cache line items
  const [loadingLineItems, setLoadingLineItems] = useState({}); // Track loading state
  const [itemRequirements, setItemRequirements] = useState([]); // Item requirements data
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [requirementsSortColumn, setRequirementsSortColumn] = useState('total_needed');
  const [requirementsSortDirection, setRequirementsSortDirection] = useState('desc');
  const ordersPerPage = 100;

  // Fetch pending fulfillment orders (with optional force refresh)
  const fetchOrders = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const url = forceRefresh
        ? '/api/pending-fulfillment?refresh=true'
        : '/api/pending-fulfillment';
      const response = await axios.get(url);
      setOrders(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch pending fulfillment orders:', err);
      setError('Failed to load pending fulfillment orders');
    } finally {
      setLoading(false);
    }
  };

  // Fetch item requirements
  const fetchItemRequirements = async () => {
    setLoadingRequirements(true);
    try {
      const response = await axios.get('/api/pending-fulfillment/item-requirements');
      setItemRequirements(response.data);
    } catch (err) {
      console.error('Failed to fetch item requirements:', err);
      setError('Failed to load item requirements');
    } finally {
      setLoadingRequirements(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, []);

  // Fetch item requirements when switching to requirements tab
  useEffect(() => {
    if (activeTab === 'requirements' && itemRequirements.length === 0) {
      fetchItemRequirements();
    }
  }, [activeTab]);

  // Handle column sort
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter orders based on shippability
  const filteredOrders = orders.filter(order => {
    if (filterShippable === 'all') return true;

    const isFullyShippable = order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0;
    const isPartiallyShippable = order.shippableLineItems > 0 && order.shippableLineItems < order.totalLineItems;
    const isNotShippable = order.shippableLineItems === 0 && order.totalLineItems > 0;

    if (filterShippable === 'fully') return isFullyShippable;
    if (filterShippable === 'partial') return isPartiallyShippable;
    if (filterShippable === 'none') return isNotShippable;

    return true;
  });

  // Sort filtered orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Handle dates
    if (sortColumn === 'tranDate' || sortColumn === 'shipDate') {
      aVal = new Date(aVal || 0);
      bVal = new Date(bVal || 0);
    }

    // Handle numbers
    if (sortColumn === 'total' || sortColumn === 'age' || sortColumn === 'shippableLineItems') {
      aVal = parseFloat(aVal || 0);
      bVal = parseFloat(bVal || 0);
    }

    // Handle strings
    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Handle numbers and dates
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  // Calculate totals
  const totalShippableOrders = sortedOrders.filter(order =>
    order.shippableLineItems > 0
  ).length;

  const fullyShippableOrders = sortedOrders.filter(order =>
    order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0
  ).length;

  // Pagination controls
  const goToPage = (pageNumber) => {
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
  };

  // Open order in NetSuite
  const openInNetSuite = (e, orderId) => {
    e.stopPropagation();
    const netsuiteUrl = `https://3646798.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${orderId}`;
    window.open(netsuiteUrl, '_blank');
  };

  // Toggle order expansion and fetch line items if needed
  const toggleOrderExpansion = async (orderId) => {
    const isExpanded = expandedOrders[orderId];

    if (!isExpanded && !orderLineItems[orderId]) {
      // Fetch line items if not already cached
      setLoadingLineItems({ ...loadingLineItems, [orderId]: true });
      try {
        const response = await axios.get(`/api/pending-fulfillment/${orderId}/line-items`);
        setOrderLineItems({ ...orderLineItems, [orderId]: response.data });
      } catch (err) {
        console.error(`Failed to fetch line items for order ${orderId}:`, err);
      } finally {
        setLoadingLineItems({ ...loadingLineItems, [orderId]: false });
      }
    }

    setExpandedOrders({ ...expandedOrders, [orderId]: !isExpanded });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="loading-container">
        <div className="loader">Loading pending fulfillment orders...</div>
      </div>
    );
  }

  // Sort requirements
  const sortedRequirements = [...itemRequirements].sort((a, b) => {
    let aVal = a[requirementsSortColumn];
    let bVal = b[requirementsSortColumn];

    // Handle strings
    if (typeof aVal === 'string') {
      return requirementsSortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Handle numbers
    return requirementsSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Handle requirements sort
  const handleRequirementsSort = (column) => {
    if (requirementsSortColumn === column) {
      setRequirementsSortDirection(requirementsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRequirementsSortColumn(column);
      setRequirementsSortDirection('asc');
    }
  };

  return (
    <div className="pending-fulfillment-list">

      {/* Info Blocks */}
      <div className="info-blocks">
        <div className="info-block">
          <div className="info-value">{sortedOrders.length}</div>
          <div className="info-label">Total Orders</div>
        </div>
        <div className="info-block">
          <div className="info-value">{totalShippableOrders}</div>
          <div className="info-label">Shippable Orders</div>
        </div>
        <div className="info-block">
          <div className="info-value">{fullyShippableOrders}</div>
          <div className="info-label">Fully Shippable</div>
        </div>
        <div className="info-block">
          <div className="info-value">{formatCurrency(sortedOrders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0))}</div>
          <div className="info-label">Total Value</div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Table Container with Integrated Tabs and Filter */}
      <div className="table-container">
        <div className="tabs-header">
          <div className="tabs-navigation">
            <button
              className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Orders
            </button>
            <button
              className={`tab-button ${activeTab === 'requirements' ? 'active' : ''}`}
              onClick={() => setActiveTab('requirements')}
            >
              Item Requirements
            </button>

            {/* Filter Control - Only show for Orders tab */}
            {activeTab === 'orders' && (
              <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center' }}>
                <select
                  value={filterShippable}
                  onChange={(e) => { setFilterShippable(e.target.value); setCurrentPage(1); }}
                  className="filter-select"
                  style={{ padding: '6px 12px', fontSize: '14px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
                >
                  <option value="all">All Orders</option>
                  <option value="fully">Fully Shippable</option>
                  <option value="partial">Partially Shippable</option>
                  <option value="none">Not Shippable</option>
                </select>
              </div>
            )}
          </div>
        </div>

      {/* Orders Tab Content */}
      {activeTab === 'orders' && (
        <>
        <table className="fulfillment-table">
          <thead>
            <tr>
              <th onClick={() => handleColumnSort('tranId')} style={{cursor: 'pointer'}}>
                Order # {sortColumn === 'tranId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('customerName')} style={{cursor: 'pointer'}}>
                Customer Name {sortColumn === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('total')} style={{cursor: 'pointer'}}>
                Amount {sortColumn === 'total' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('tranDate')} style={{cursor: 'pointer'}}>
                Order Date {sortColumn === 'tranDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('shipDate')} style={{cursor: 'pointer'}}>
                Ship Date {sortColumn === 'shipDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('age')} style={{cursor: 'pointer'}}>
                Age {sortColumn === 'age' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('shippableLineItems')} style={{cursor: 'pointer'}}>
                Shippable Items {sortColumn === 'shippableLineItems' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => {
              const isFullyShippable = order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0;
              const isPartiallyShippable = order.shippableLineItems > 0 && order.shippableLineItems < order.totalLineItems;
              const isNotShippable = order.shippableLineItems === 0 && order.totalLineItems > 0;
              const isExpanded = expandedOrders[order.id];
              const lineItems = orderLineItems[order.id] || [];
              const isLoadingItems = loadingLineItems[order.id];

              return (
                <React.Fragment key={order.id}>
                <tr
                  className="clickable-row"
                  onClick={() => toggleOrderExpansion(order.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="order-number">
                    <span style={{ marginRight: '8px', display: 'inline-block', width: '12px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <a
                      href="#"
                      onClick={(e) => openInNetSuite(e, order.id)}
                      style={{color: '#2c5282', textDecoration: 'none'}}
                      title="Open in NetSuite"
                    >
                      {order.tranId}
                    </a>
                  </td>
                  <td>
                    {order.customerName || 'N/A'}
                    {order.customerCategory === 26 && (
                      <span className="customer-badge customer-select" title="Select Customer"> SELECT</span>
                    )}
                    {order.customerCategory === 39 && (
                      <span className="customer-badge customer-private" title="Private Label"> PRIVATE LABEL</span>
                    )}
                  </td>
                  <td className="amount">{formatCurrency(order.total)}</td>
                  <td>{formatDate(order.tranDate)}</td>
                  <td>{order.shipDate ? formatDate(order.shipDate) : 'N/A'}</td>
                  <td className={`age-cell ${order.age > 0 ? 'overdue' : order.age === 0 ? 'due-today' : ''}`}>
                    {order.age !== null ? (
                      order.age > 0 ? `${order.age} days overdue` :
                      order.age === 0 ? 'Due today' :
                      `${Math.abs(order.age)} days`
                    ) : 'N/A'}
                  </td>
                  <td className="shippable-cell">
                    <span className={`shippable-badge ${
                      isFullyShippable ? 'shippable-full' :
                      isPartiallyShippable ? 'shippable-partial' :
                      isNotShippable ? 'shippable-none' : ''
                    }`}>
                      {order.shippableLineItems || 0} / {order.totalLineItems || 0}
                      {isFullyShippable && ' ✓'}
                    </span>
                  </td>
                  <td>
                    {order.status === 'D' ? (
                      <span className="status-badge status-partial">Partially Fulfilled</span>
                    ) : (
                      ''
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="expanded-row">
                    <td colSpan="8" style={{ padding: 0 }}>
                      <div style={{ padding: '10px 20px', backgroundColor: '#f9fafb' }}>
                        {isLoadingItems ? (
                          <div>Loading line items...</div>
                        ) : lineItems.length > 0 ? (
                          <table style={{ width: '100%', fontSize: '0.9em' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#e5e7eb' }}>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Item SKU</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Description</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Ordered</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Fulfilled</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Remaining</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Available</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lineItems.map((item, index) => {
                                const rowColor = item.isFullyFulfilled
                                  ? '#e5e7eb' // Gray for fulfilled
                                  : item.isShippable
                                    ? '#d1fae5' // Light green for shippable
                                    : '#fee2e2'; // Light red for not shippable

                                return (
                                  <tr key={index} style={{ backgroundColor: rowColor }}>
                                    <td style={{ padding: '6px 8px' }}>{item.itemid}</td>
                                    <td style={{ padding: '6px 8px' }}>{item.displayname || item.itemid}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.quantity}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.fulfilled_qty}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.remaining_qty}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.available_qty}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <div>No line items found</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {sortedOrders.length === 0 && !loading && (
          <div className="no-data">
            No pending fulfillment orders found
          </div>
        )}
        </>
      )}

      {/* Item Requirements Tab Content */}
      {activeTab === 'requirements' && (
        loadingRequirements ? (
          <div className="loading-container">
            <div className="loader">Loading item requirements...</div>
          </div>
        ) : (
          <table className="fulfillment-table">
              <thead>
                <tr>
                  <th onClick={() => handleRequirementsSort('itemid')}>
                    Item SKU {requirementsSortColumn === 'itemid' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleRequirementsSort('displayname')}>
                    Description {requirementsSortColumn === 'displayname' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleRequirementsSort('total_needed')} className="amount">
                    Total Needed {requirementsSortColumn === 'total_needed' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleRequirementsSort('available_qty')} className="amount">
                    Available {requirementsSortColumn === 'available_qty' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleRequirementsSort('shortage')} className="amount">
                    Shortage {requirementsSortColumn === 'shortage' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleRequirementsSort('order_count')} className="amount">
                    Orders {requirementsSortColumn === 'order_count' && (requirementsSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRequirements.map((item, index) => {
                  const hasShortage = item.has_shortage;

                  return (
                    <tr key={index}>
                      <td className="order-number">{item.itemid}</td>
                      <td>{item.displayname || item.itemid}</td>
                      <td className="amount">
                        {Math.round(item.total_needed)}
                      </td>
                      <td className="amount">
                        {Math.round(item.available_qty)}
                      </td>
                      <td className="amount">
                        <span className={`shippable-badge ${hasShortage ? 'shippable-none' : 'shippable-full'}`}>
                          {hasShortage ? Math.round(item.shortage) : '✓'}
                        </span>
                      </td>
                      <td className="amount">
                        {item.order_count}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        )
      )}
      {activeTab === 'requirements' && sortedRequirements.length === 0 && !loadingRequirements && (
        <div className="no-data">
          No item requirements found
        </div>
      )}
      </div>

      {/* Pagination Controls - Only show for Orders tab */}
      {activeTab === 'orders' && totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            First
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({sortedOrders.length} total orders)
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default PendingFulfillmentList;
